/* ────────────────────────────────────────────────────────────
 * chatAttachments — the chat's attachment rules (QA CM-07), which
 * attachment URLs are photos, and the display names of stored files
 * (QA CM-08).
 *
 * One module so the rule shown to the user, the checks and the file
 * input's `accept` can never drift apart. The numbers copy the Add
 * sources dialog (FileManagement/DataSets/AddSourceModal.js) and are
 * proposals: they must match what the chat server enforces on
 * POST /chat/private-chat/handleUpload, so change them here only. The
 * web checks don't protect the server — the mobile app and direct API
 * calls bypass them.
 * ──────────────────────────────────────────────────────────── */

// No svg, html, exe or scripts: anything that can run or be rendered as
// a page when opened.
export const CHAT_ALLOWED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "jfif",
  "png",
  "gif",
  "webp",
  "bmp",
  "mp4",
  "webm",
  "mov",
  "mp3",
  "m4a",
  "wav",
  "ogg",
  "aac",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "ppt",
  "pptx",
  "txt",
  "zip",
];

export const CHAT_ATTACHMENT_ACCEPT = CHAT_ALLOWED_EXTENSIONS.map(
  (ext) => `.${ext}`
).join(",");

export const CHAT_MAX_FILE_BYTES = 25 * 1024 * 1024;
export const CHAT_MAX_FILES = 10;

const MAX_SIZE_LABEL = `${CHAT_MAX_FILE_BYTES / (1024 * 1024)} MB`;

export const CHAT_ATTACHMENT_RULE = `Photos, videos, audio, PDF, Word, Excel, PowerPoint, CSV, text or ZIP · up to ${MAX_SIZE_LABEL} each · up to ${CHAT_MAX_FILES} files per message`;

// The attach menu item's name, so the limits are heard before the first pick.
export const CHAT_ATTACH_LABEL = `Attach files (up to ${MAX_SIZE_LABEL} each, ${CHAT_MAX_FILES} per message)`;

export const formatBytes = (bytes) => {
  if (bytes == null || isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Lower-cased text after the last dot of a file name, '' when there is none.
const extensionOf = (name) => {
  const text = String(name || "");
  const dot = text.lastIndexOf(".");
  return dot >= 0 ? text.slice(dot + 1).toLowerCase() : "";
};

/**
 * Splits picked, dropped, pasted or recorded files into the ones that
 * may be attached and the ones that may not, with the reason for each.
 * `stagedCount` is how many files are already waiting in the tray.
 */
export const validateChatFiles = (files, stagedCount = 0) => {
  const accepted = [];
  const rejected = [];
  (files || []).forEach((file) => {
    if (!file) return;
    const name = file.name || "Untitled";
    const ext = extensionOf(file.name);
    let reason = "";
    if (!ext || !CHAT_ALLOWED_EXTENSIONS.includes(ext)) {
      reason = ext ? `.${ext} files aren't allowed` : "this file type isn't allowed";
    } else if (file.size === 0) {
      reason = "the file is empty";
    } else if (file.size > CHAT_MAX_FILE_BYTES) {
      reason = `it's ${formatBytes(file.size)}, the limit is ${MAX_SIZE_LABEL}`;
    } else if (stagedCount + accepted.length >= CHAT_MAX_FILES) {
      reason = `only ${CHAT_MAX_FILES} files can go in one message`;
    }
    if (reason) rejected.push({ name, reason });
    else accepted.push(file);
  });
  return { accepted, rejected };
};

// One readable sentence for the tray notice; '' when nothing was refused.
export const describeRejected = (rejected) => {
  if (!Array.isArray(rejected) || !rejected.length) return "";
  if (rejected.length === 1) {
    return `"${rejected[0].name}" wasn't added: ${rejected[0].reason}.`;
  }
  const listed = rejected
    .slice(0, 3)
    .map((item) => `"${item.name}" (${item.reason})`)
    .join(", ");
  const more = rejected.length > 3 ? ` and ${rejected.length - 3} more` : "";
  return `${rejected.length} files weren't added: ${listed}${more}.`;
};

// ── Photos ─────────────────────────────────────────────────────
// Moved here from MessageList so messageStatus.markDeleted can tell a
// photo row apart too (QA CM-03).
//
// Anchored extension match — we deliberately require the dot+ext to sit at the
// real end of the URL path (followed by `?`, `#`, end-of-string, or `/`) so a
// presigned S3 URL like `.../foo.jpg?signature=...` still resolves to "image"
// and a directory segment like `/png/` doesn't accidentally match. The
// extension list intentionally covers the long tail of formats we've seen
// real users paste/upload — Windows screenshots are often saved as `.jfif`,
// iOS shares are `.heic`/`.heif`, modern web exports use `.avif`.
const IMAGE_URL_RE =
  /\.(png|jpe?g|jfif|pjpeg|gif|webp|bmp|svg|heic|heif|avif|tiff?|ico)(\?|#|$|\/)/i;

// Some presigned URLs come through with an opaque path (no extension at all)
// but include the MIME type as a `response-content-type=image%2F...` query
// parameter. Trust that as a secondary signal so the lightbox burst doesn't
// skip those rows.
const IMAGE_MIME_QUERY_RE =
  /[?&](?:response-content-type|content-type)=image%2[Ff]/i;

export const isImageUrl = (url) =>
  typeof url === "string" &&
  (IMAGE_URL_RE.test(url) || IMAGE_MIME_QUERY_RE.test(url));

// ── Display names (QA CM-08) ───────────────────────────────────
// The chat server stores every upload as '<name>-<Date.now()>.<ext>'
// and that stored name is the only one any client ever gets back. The
// suffix is hidden for display only — hrefs and Resource_Path are signed
// URLs and are never rewritten.
const STORED_SUFFIX_RE = /-(\d{13})(?=(?:\.[^./]*)?$)/;
// A Date.now() value between 2017 and 2100, so a 13-digit number that is
// part of a real name is left alone.
const STAMP_FROM = 1483228800000;
const STAMP_TO = 4102444800000;

export const displayFileName = (stored) => {
  if (typeof stored !== "string" || !stored) return stored;
  const match = STORED_SUFFIX_RE.exec(stored);
  if (!match) return stored;
  const stamp = Number(match[1]);
  if (stamp < STAMP_FROM || stamp > STAMP_TO) return stored;
  const name = stored.slice(0, match.index) + stored.slice(match.index + match[0].length);
  if (!name || name.startsWith(".")) return stored;
  return name;
};

/**
 * `{ name, ext }` for an attachment URL, the same shape the old per-file
 * getFileMeta copies returned. `originalName` wins when the server sends
 * one (not yet: see the backend notes); otherwise the last path segment,
 * with the upload-time suffix hidden.
 */
export const getAttachmentMeta = (url, originalName) => {
  let segment = "";
  if (typeof url === "string" && url) {
    const cleanPath = url.split("?")[0].split("#")[0];
    segment = cleanPath.split("/").pop() || "";
    try {
      segment = decodeURIComponent(segment);
    } catch (_) {
      // keep the raw segment when it isn't valid percent-encoding
    }
  }
  const name =
    (typeof originalName === "string" ? originalName.trim() : "") ||
    displayFileName(segment) ||
    "Document";
  const dot = name.lastIndexOf(".");
  const ext = (dot >= 0 ? name.slice(dot + 1).toUpperCase() : "") || "FILE";
  return { name, ext };
};
